import { useMutation, useQuery } from "@tanstack/react-query";
import {
  openBillingPortal,
  startCheckout,
  startEmbeddedCheckout,
  type SubscriptionState,
} from "@/api/payments";
import { subscriptionStatusQuery } from "@/lib/appQueries";

/** Current subscription state — drives the trial banner and the paywall. */
export function useSubscriptionStatus(options?: { enabled?: boolean }) {
  return useQuery<SubscriptionState>({
    ...subscriptionStatusQuery,
    enabled: options?.enabled ?? true,
    // SubscriptionGate fails OPEN on purpose: a failed status call renders the
    // page rather than replacing it, because entitlement is enforced
    // server-side. Reporting the same failure as a page-level load error would
    // put a warning on every route over a check the app has already decided
    // does not matter (BEN-141 QF4).
    meta: { failureNotice: false },
  });
}

/** Start Stripe Checkout, then hand the browser off to the Stripe-hosted page. */
export function useStartCheckout() {
  return useMutation({
    mutationFn: (plan: "monthly" | "yearly") => startCheckout(plan),
    onSuccess: (url) => {
      window.location.assign(url);
    },
  });
}

/** AN-29: start an embedded Checkout; the caller mounts the returned client secret. */
export function useStartEmbeddedCheckout() {
  return useMutation({
    mutationFn: (plan: "monthly" | "yearly") => startEmbeddedCheckout(plan),
    // The plan page reports a failure itself, beside the button (an already-active
    // subscription reads differently from a network failure there).
    meta: {
      write: { action: "start checkout", unchanged: "No trial was started.", report: "inline" },
    },
  });
}

/** Open the Stripe Customer Portal (manage billing / cancel). */
export function useOpenBillingPortal() {
  return useMutation({
    mutationFn: openBillingPortal,
    onSuccess: (url) => {
      window.location.assign(url);
    },
  });
}
